<template>
    <div class="ad-manager">
        <el-card shadow="never" class="content-card">
            <template #header>
                <div class="card-header">
                    <div class="header-title">
                        <el-icon>
                            <Promotion />
                        </el-icon>
                        <span>广告卡片管理</span>
                    </div>
                    <el-button type="primary" @click="openCreateDialog">
                        <el-icon>
                            <Plus />
                        </el-icon> 新建广告
                    </el-button>
                </div>
            </template>

            <el-table :data="adCards" v-loading="loading" style="width: 100%" class="custom-table">
                <el-table-column prop="id" label="ID" width="70" align="center" />
                <el-table-column label="图片" width="120" align="center">
                    <template #default="{ row }">
                        <el-image v-if="row.imageUrl" :src="row.imageUrl" :preview-src-list="[row.imageUrl]" fit="cover"
                            style="width: 80px; height: 50px; border-radius: 6px;" />
                        <span v-else class="no-image">无图片</span>
                    </template>
                </el-table-column>
                <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
                <el-table-column prop="tags" label="标签" min-width="140" show-overflow-tooltip>
                    <template #default="{ row }">
                        <el-tag v-for="tag in (row.tags || '').split(',').filter(t => t.trim())" :key="tag" size="small"
                            type="info" effect="plain" style="margin: 2px;">
                            {{ tag.trim() }}
                        </el-tag>
                        <span v-if="!row.tags">-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="price" label="价格" width="100" align="center">
                    <template #default="{ row }">
                        <span v-if="row.price" style="color: #f43f5e; font-weight: 700;">¥{{ row.price }}</span>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="badgeText" label="角标" width="100" align="center">
                    <template #default="{ row }">
                        <el-tag v-if="row.badgeText" size="small" type="warning">{{ row.badgeText }}</el-tag>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="sortOrder" label="排序" width="80" align="center" />
                <el-table-column label="状态" width="100" align="center">
                    <template #default="{ row }">
                        <el-switch v-model="row.isPublished" @change="handleTogglePublish(row)" active-text="上架"
                            inactive-text="下架" inline-prompt />
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="140" fixed="right" align="center">
                    <template #default="{ row }">
                        <el-button link type="primary" @click="openEditDialog(row)">编辑</el-button>
                        <el-button link type="danger" @click="handleDelete(row)">删除</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- Create / Edit Dialog -->
        <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑广告' : '新建广告'" width="560px" class="custom-dialog">
            <el-form :model="form" label-position="top">
                <el-form-item label="广告标题" required>
                    <el-input v-model="form.title" placeholder="例如：基础-提高衔接计划" />
                </el-form-item>
                <el-form-item label="图片 URL">
                    <el-input v-model="form.imageUrl" placeholder="广告展示图片链接" />
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="标签 (逗号分隔)">
                            <el-input v-model="form.tags" placeholder="例如：刷题练习,冲刺高分" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="价格">
                            <el-input v-model="form.price" placeholder="例如：3999" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="跳转链接">
                    <el-input v-model="form.linkUrl" placeholder="点击广告后的跳转地址" />
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="角标文字">
                            <el-input v-model="form.badgeText" placeholder="例如：官方推荐" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="排序 (越大越靠前)">
                            <el-input-number v-model="form.sortOrder" :min="0" :max="9999" style="width: 100%;" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="发布状态">
                    <el-switch v-model="form.isPublished" active-text="上架" inactive-text="下架" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="handleSave">
                    {{ isEdit ? '保存修改' : '立即创建' }}
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Promotion } from '@element-plus/icons-vue'
import api from '@/utils/api'

const loading = ref(false)
const saving = ref(false)
const adCards = ref([])
const dialogVisible = ref(false)
const isEdit = ref(false)
const editId = ref(null)

const form = reactive({
    title: '',
    imageUrl: '',
    tags: '',
    price: '',
    linkUrl: '',
    badgeText: '',
    sortOrder: 0,
    isPublished: false
})

const resetForm = () => {
    form.title = ''
    form.imageUrl = ''
    form.tags = ''
    form.price = ''
    form.linkUrl = ''
    form.badgeText = ''
    form.sortOrder = 0
    form.isPublished = false
}

const fetchAdCards = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/ad-cards')
        adCards.value = res.data.adCards || []
    } catch (err) {
        ElMessage.error('获取广告列表失败')
    } finally {
        loading.value = false
    }
}

const openCreateDialog = () => {
    isEdit.value = false
    editId.value = null
    resetForm()
    dialogVisible.value = true
}

const openEditDialog = (row) => {
    isEdit.value = true
    editId.value = row.id
    form.title = row.title
    form.imageUrl = row.imageUrl
    form.tags = row.tags
    form.price = row.price
    form.linkUrl = row.linkUrl
    form.badgeText = row.badgeText
    form.sortOrder = row.sortOrder
    form.isPublished = row.isPublished
    dialogVisible.value = true
}

const handleSave = async () => {
    if (!form.title.trim()) {
        return ElMessage.warning('请输入广告标题')
    }
    saving.value = true
    try {
        if (isEdit.value) {
            await api.put(`/api/admin/ad-cards/${editId.value}`, { ...form })
            ElMessage.success('更新成功')
        } else {
            await api.post('/api/admin/ad-cards', { ...form })
            ElMessage.success('创建成功')
        }
        dialogVisible.value = false
        fetchAdCards()
    } catch (err) {
        ElMessage.error(isEdit.value ? '更新失败' : '创建失败')
    } finally {
        saving.value = false
    }
}

const handleTogglePublish = async (row) => {
    try {
        await api.put(`/api/admin/ad-cards/${row.id}`, { ...row })
        ElMessage.success(row.isPublished ? '已上架' : '已下架')
    } catch (err) {
        row.isPublished = !row.isPublished
        ElMessage.error('操作失败')
    }
}

const handleDelete = (row) => {
    ElMessageBox.confirm('确定要删除该广告吗？此操作不可撤销。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async () => {
        await api.delete(`/api/admin/ad-cards/${row.id}`)
        ElMessage.success('删除成功')
        fetchAdCards()
    }).catch(() => { })
}

onMounted(() => {
    fetchAdCards()
})
</script>

<style scoped>
.ad-manager {
    padding: 0;
}

.content-card {
    border-radius: 12px;
    border: 1px solid #e6e6e6;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: #303133;
}

.no-image {
    font-size: 12px;
    color: #c0c4cc;
}

.custom-table :deep(.el-table__header th) {
    background-color: #fafafa;
    color: #606266;
    font-weight: 600;
}
</style>
